/* ====================================================================
   RMS Product — interactive system viewer
   Vanilla JS, no dependencies. Handles tier inspection, mechanics
   highlighting, BEGIN cycle animation, deliverables modal,
   reduced-motion preferences, and modal focus management.

   Phase 3c-1: core inspect interaction ✓
   Phase 3c-2: mechanics highlight + close button ✓
   Phase 3c-3: BEGIN button cycle + deliverables modal ✓
   Phase 3d:   mobile auto-scroll on tier select ✓
   Phase 3e:   button cycling state, focus management,
               focus trap, reduced-motion support ✓
   ==================================================================== */

(function () {
  'use strict';

  const viewer = document.querySelector('[data-rms-viewer]');
  if (!viewer) return;

  const modules = viewer.querySelectorAll('.rms-module');
  const panels = viewer.querySelectorAll('.rms-panel');
  const closeButtons = viewer.querySelectorAll('.rms-panel__close');
  const mechanics = document.querySelectorAll('.rms-mechanic');
  const beginButton = document.querySelector('[data-rms-begin]');
  const beginButtonOriginalText = beginButton ? beginButton.textContent : '';
  const modal = document.querySelector('[data-rms-modal]');

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';

  const mechanicMapping = {
    quarter: ['arena', 'ccni'],
    month: [],
    week: [],
    day: ['genus', 'bridge', 'electional', 'rpg']
  };

  let cycling = false;
  let lastFocusedElement = null;

  /* ---- Mechanic highlighting ---- */

  function highlightMechanics(tier) {
    clearMechanicHighlights();
    const related = mechanicMapping[tier] || [];
    if (related.length === 0) return;
    mechanics.forEach((m) => {
      const mech = m.dataset.mechanic;
      if (related.includes(mech)) {
        m.classList.add('is-highlighted');
      } else {
        m.classList.add('is-dimmed');
      }
    });
  }

  function clearMechanicHighlights() {
    mechanics.forEach((m) => {
      m.classList.remove('is-highlighted');
      m.classList.remove('is-dimmed');
    });
  }

  /* ---- Tier selection ---- */

  function selectTier(tier) {
    modules.forEach((m) => {
      const t = m.dataset.tier;
      m.classList.toggle('is-active', t === tier);
      m.classList.toggle('is-dimmed', t !== tier);
    });

    panels.forEach((p) => {
      const t = p.dataset.panel;
      p.classList.toggle('is-active', t === tier);
    });

    highlightMechanics(tier);

    if (!cycling && window.innerWidth < 990) {
      const activePanel = viewer.querySelector('.rms-panel.is-active');
      if (activePanel) {
        activePanel.scrollIntoView({ behavior: scrollBehavior, block: 'nearest' });
      }
    }
  }

  function deselectAll() {
    modules.forEach((m) => {
      m.classList.remove('is-active');
      m.classList.remove('is-dimmed');
    });
    panels.forEach((p) => {
      p.classList.toggle('is-active', p.dataset.panel === 'default');
    });
    clearMechanicHighlights();
  }

  /* ---- Focus management ---- */

  function getFocusableElements(container) {
    return container.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), ' +
      'select:not([disabled]), textarea:not([disabled]), ' +
      '[tabindex]:not([tabindex="-1"])'
    );
  }

  /* ---- Modal ---- */

  function openModal() {
    if (!modal) return;
    lastFocusedElement = document.activeElement;
    modal.classList.add('is-active');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('rms-modal-open');

    const focusables = getFocusableElements(modal);
    if (focusables.length) {
      focusables[0].focus();
    }
  }

  function closeModal(scrollToBuy) {
    if (!modal) return;
    modal.classList.remove('is-active');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('rms-modal-open');

    if (beginButton) {
      beginButton.classList.remove('is-cycling');
      beginButton.textContent = beginButtonOriginalText;
    }

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
    lastFocusedElement = null;

    /* Only scroll to the buy box when the user explicitly chose
       to continue via [ BACK TO BUY ↑ ]. X, backdrop, and Escape
       are opt-outs — leave the page where it is. */
    if (scrollToBuy) {
      const buyBox = document.querySelector('.rms-fold');
      if (buyBox) {
        buyBox.scrollIntoView({ behavior: scrollBehavior, block: 'start' });
      }
    }
  }

  /* ---- BEGIN cycle ---- */

  const tierSequence = ['quarter', 'month', 'week', 'day'];
  const cycleStepDuration = prefersReducedMotion ? 200 : 600;

  function cycleTiers(onComplete) {
    cycling = true;
    let i = 0;
    function next() {
      if (i >= tierSequence.length) {
        deselectAll();
        cycling = false;
        if (onComplete) onComplete();
        return;
      }
      selectTier(tierSequence[i]);
      i++;
      setTimeout(next, cycleStepDuration);
    }
    next();
  }

  /* ---- Event wiring ---- */

  modules.forEach((module) => {
    module.addEventListener('click', () => {
      if (cycling) return;
      const tier = module.dataset.tier;
      if (module.classList.contains('is-active')) {
        deselectAll();
      } else {
        selectTier(tier);
      }
    });
  });

  closeButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      if (cycling) return;
      deselectAll();
    });
  });

  if (beginButton) {
    beginButton.addEventListener('click', (e) => {
      e.preventDefault();
      if (cycling) return;
      if (modal && modal.classList.contains('is-active')) return;

      beginButton.classList.add('is-cycling');
      beginButton.textContent = '[ CYCLING... ]';

      const scrollTarget = document.querySelector('.rms-viewer__header') || viewer;
      scrollTarget.scrollIntoView({ behavior: scrollBehavior, block: 'start' });

      const initialDelay = prefersReducedMotion ? 0 : 500;
      const postCycleDelay = prefersReducedMotion ? 0 : 400;

      setTimeout(() => {
        cycleTiers(() => {
          setTimeout(openModal, postCycleDelay);
        });
      }, initialDelay);
    });
  }

  if (modal) {
    const modalCloseBtns = modal.querySelectorAll('[data-rms-modal-close]');
    modalCloseBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        /* BACK TO BUY scrolls; X and backdrop just close. */
        const scrollToBuy = btn.classList.contains('rms-modal__cta');
        closeModal(scrollToBuy);
      });
    });
  }

  /* ---- Keyboard ---- */

  document.addEventListener('keydown', (e) => {
    /* Modal focus trap: Tab cycles within modal */
    if (modal && modal.classList.contains('is-active') && e.key === 'Tab') {
      const focusables = getFocusableElements(modal);
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
      return;
    }

    /* Escape: close modal first, then deselect tier */
    if (e.key === 'Escape') {
      if (cycling) return;

      if (modal && modal.classList.contains('is-active')) {
        closeModal(false);
        return;
      }

      const hasActive = viewer.querySelector('.rms-module.is-active');
      if (hasActive) deselectAll();
    }
  });
})();